import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { VaultItemDTO, WarbandMemberDTO } from '../../types';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { vaultService } from '../../services/vaultService';

interface ManageMemberItemsModalProps {
  open: boolean;
  warbandId: string;
  member: WarbandMemberDTO;
  onClose: () => void;
}

type ItemCategory = 'general' | 'scrolls' | 'potions' | 'grimoires';

const CATEGORIES: { key: ItemCategory; label: string }[] = [
  { key: 'general', label: 'Magic Items' },
  { key: 'scrolls', label: 'Scrolls' },
  { key: 'potions', label: 'Potions' },
  { key: 'grimoires', label: 'Grimoires' },
];

function categoriseItem(item: VaultItemDTO): ItemCategory {
  const id = item.id.toLowerCase();
  if (id.startsWith('scroll_')) return 'scrolls';
  if (id.startsWith('grimoire_')) return 'grimoires';
  if (item.baseItemType === 'potion') return 'potions';
  return 'general';
}

export const ManageMemberItemsModal: React.FC<ManageMemberItemsModalProps> = ({
  open,
  warbandId,
  member,
  onClose,
}) => {
  const updateMemberTreasureItems = useDirtyTrackingStore((s) => s.updateMemberTreasureItems);
  const updateMemberWeapons = useDirtyTrackingStore((s) => s.updateMemberWeapons);
  const updateMemberArmours = useDirtyTrackingStore((s) => s.updateMemberArmours);
  const rawVaultItems = useWarbandStore((s) => s.vaultItems);
  const updateVaultItemMemberId = useWarbandStore((s) => s.updateVaultItemMemberId);
  const wizardData = useWarbandStore((s) => s.wizardData);
  const apprenticeData = useWarbandStore((s) => s.apprenticeData);
  const soldiersData = useWarbandStore((s) => s.soldiersData);

  const [error, setError] = useState<string | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open) return;
    setSelectedInstanceId(null);
    setFilter('');
    setError(null);
    setActiveTab(0);
  }, [open]);

  // Compute occupied vault instance IDs.
  // Phase 1 (direct): instances with warbandMemberId set to a different member are occupied.
  // Phase 2 (heuristic): for instances without warbandMemberId set, fall back to the
  //   count-based approach — covers existing saves where warbandMemberId was never written.
  const occupiedInstanceIds = useMemo(() => {
    const occupied = new Set<string>();

    // Phase 1: directly tracked assignments
    const trackedCounts = new Map<string, number>();
    for (const item of rawVaultItems) {
      if (!item.instanceId) continue;
      if (item.warbandMemberId !== null && item.warbandMemberId !== member.id) {
        occupied.add(item.instanceId);
      }
      if (item.warbandMemberId !== null) {
        trackedCounts.set(item.id, (trackedCounts.get(item.id) ?? 0) + 1);
      }
    }

    // Phase 2: heuristic for untracked (warbandMemberId === null) instances
    const allMemberItems = [
      ...(wizardData?.treasureItems ?? []),
      ...(apprenticeData?.treasureItems ?? []),
      ...(soldiersData?.soldiers.flatMap((s) => s.treasureItems ?? []) ?? []),
    ];
    const totalCounts = new Map<string, number>();
    for (const item of allMemberItems) {
      totalCounts.set(item.id, (totalCounts.get(item.id) ?? 0) + 1);
    }

    const untrackedByType = new Map<string, VaultItemDTO[]>();
    for (const item of rawVaultItems) {
      if (!item.instanceId || item.warbandMemberId !== null) continue;
      const bucket = untrackedByType.get(item.id) ?? [];
      bucket.push(item);
      untrackedByType.set(item.id, bucket);
    }

    untrackedByType.forEach((instances, typeId) => {
      const total = totalCounts.get(typeId) ?? 0;
      const tracked = trackedCounts.get(typeId) ?? 0;
      const remaining = Math.max(0, total - tracked);
      for (let i = 0; i < Math.min(remaining, instances.length); i++) {
        occupied.add(instances[i].instanceId!);
      }
    });

    return occupied;
  }, [rawVaultItems, member.id, wizardData, apprenticeData, soldiersData]);

  const allItems = useMemo(
    () => [...rawVaultItems]
      .filter((item) =>
        item.instanceId !== null &&
        item.warbandMemberId !== member.id &&
        !occupiedInstanceIds.has(item.instanceId!)
      )
      .sort((a, b) => a.name.localeCompare(b.name)),
    [rawVaultItems, occupiedInstanceIds, member.id]
  );

  const memberRole: 'wizard' | 'apprentice' | 'soldier' =
    member.creatureId?.toLowerCase() === 'wizard' ? 'wizard'
      : member.creatureId?.toLowerCase() === 'apprentice' ? 'apprentice'
        : 'soldier';

  const isSpellcaster = memberRole === 'wizard' || memberRole === 'apprentice';

  const equippedBaseIds = useMemo(() => {
    const ids = new Set<string>();
    for (const w of member.weapons ?? []) ids.add(w.id);
    for (const a of member.armours ?? []) ids.add(a.id);
    return ids;
  }, [member.weapons, member.armours]);

  const categorisedItems = useMemo(() => {
    const lower = filter.trim().toLowerCase();

    // Filter out magic items with baseItemId when the base weapon/armour is not equipped
    // Exception: spellcasters can see all magic items
    const baseFiltered = allItems.filter((item) => {
      if (!item.baseItemId) return true;
      if (isSpellcaster) return true;
      return equippedBaseIds.has(item.baseItemId);
    });

    const filtered = lower
      ? baseFiltered.filter(
          (item) =>
            item.name.toLowerCase().includes(lower) ||
            (item.description?.toLowerCase().includes(lower) ?? false)
        )
      : baseFiltered;

    const groups: Record<ItemCategory, VaultItemDTO[]> = {
      general: [],
      scrolls: [],
      potions: [],
      grimoires: [],
    };
    for (const item of filtered) {
      const cat = categoriseItem(item);
      groups[cat].push(item);
    }
    return groups;
  }, [allItems, filter, isSpellcaster, equippedBaseIds]);

  const activeCategory = CATEGORIES[activeTab]?.key ?? 'general';
  const displayedItems = categorisedItems[activeCategory];

  const handleAdd = () => {
    if (!selectedInstanceId) return;
    const selectedItem = allItems.find((i) => i.instanceId === selectedInstanceId);
    if (!selectedItem) return;
    const currentIds = (member.treasureItems ?? []).map((t) => t.id);

    // For spellcasters: auto-equip the base weapon/armour if not already equipped
    if (isSpellcaster && selectedItem.baseItemId) {
      if (selectedItem.baseItemType === 'weapon' && !equippedBaseIds.has(selectedItem.baseItemId)) {
        const currentWeaponIds = (member.weapons ?? []).map((w) => w.id);
        updateMemberWeapons(member.id, memberRole as 'wizard' | 'apprentice', [...currentWeaponIds, selectedItem.baseItemId]);
      }
      if (selectedItem.baseItemType === 'armour' && !equippedBaseIds.has(selectedItem.baseItemId)) {
        const currentArmourIds = (member.armours ?? []).map((a) => a.id);
        updateMemberArmours(member.id, memberRole as 'wizard' | 'apprentice', [...currentArmourIds, selectedItem.baseItemId]);
      }
    }

    // Set parentItemId on the vault item if it has a baseItemId
    if (selectedItem.baseItemId && selectedItem.instanceId) {
      vaultService.updateVaultItemParentId(warbandId, selectedItem.instanceId, selectedItem.baseItemId);
    }

    updateMemberTreasureItems(member.id, memberRole, [...currentIds, selectedItem.id]);
    if (selectedItem.instanceId) {
      updateVaultItemMemberId(selectedItem.instanceId, member.id);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {allItems.length > 0 && (
          <>
            <TextField
              size="small"
              placeholder="Filter items…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />

            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              {CATEGORIES.map((cat) => (
                <Tab
                  key={cat.key}
                  label={`${cat.label} (${categorisedItems[cat.key].length})`}
                />
              ))}
            </Tabs>

            {displayedItems.length === 0 ? (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  No items available in this category.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ height: 240, overflowY: 'auto' }}>
                {displayedItems.map((item) => (
                  <ListItemButton
                    key={item.instanceId ?? item.id}
                    selected={selectedInstanceId === item.instanceId}
                    onClick={() => setSelectedInstanceId(item.instanceId)}
                    sx={{ borderRadius: 1, py: 0.5 }}
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={item.description}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}

        {allItems.length === 0 && !error && (
          <Typography variant="body2" color="textSecondary">
            No vault items available.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={selectedInstanceId === null}
        >
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};
